import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import DashboardLayout from '../components/Layout/DashboardLayout';
import { cardsService } from '../services/cardsService';
import { analyticsService } from '../services/analyticsService';
import { ArrowLeft, ExternalLink, Eye, MousePointerClick, Download, Trash2, Edit, Share2, Copy, Check, Send, Mail, MessageCircle, FileText, Smartphone, Wifi, ShoppingCart, X } from 'lucide-react';

const CardDetail = () => {
  const navigate = useNavigate();
  const { cardId } = useParams();
  const [card, setCard] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [nfcCopied, setNfcCopied] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showNfcModal, setShowNfcModal] = useState(false);
  const [sendEmail, setSendEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    fetchCardAndAnalytics();
  }, [cardId]);

  const fetchCardAndAnalytics = async () => {
    try {
      const [cardData, analyticsData] = await Promise.all([
        cardsService.getCard(cardId),
        analyticsService.getCardAnalytics(cardId).catch(() => null)
      ]);
      setCard(cardData);
      setAnalytics(analyticsData);
    } catch (err) {
      console.error('Error fetching card:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette carte ?')) {
      try {
        await cardsService.deleteCard(cardId);
        navigate('/dashboard');
      } catch (err) {
        alert('Erreur lors de la suppression');
      }
    }
  };

  const copyPublicUrl = () => {
    const publicUrl = `${window.location.origin}/card/${card.unique_id}`;
    navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const copyNfcUrl = () => {
    const publicUrl = `${window.location.origin}/card/${card.unique_id}`;
    navigator.clipboard.writeText(publicUrl);
    setNfcCopied(true);
    setTimeout(() => setNfcCopied(false), 2000);
  };

  const openPublicCard = () => {
    window.open(`${window.location.origin}/card/${card.unique_id}`, '_blank');
  };

  const handleSendToAdvisor = async () => {
    if (!sendEmail) {
      alert('Veuillez entrer l\'email du conseiller');
      return;
    }
    
    setSending(true);
    
    try {
      await cardsService.sendCardEmail(card.id, sendEmail);
      setSendSuccess(true);
      setTimeout(() => {
        setShowSendModal(false);
        setSendSuccess(false);
        setSendEmail('');
      }, 2000);
    } catch (error) {
      console.error('Failed to send email:', error);
      alert('Erreur lors de l\'envoi de l\'email: ' + (error.response?.data?.detail || 'Erreur inconnue'));
    } finally {
      setSending(false);
    }
  };

  const handleSendByWhatsApp = () => {
    const publicUrl = `${window.location.origin}/card/${card.unique_id}`;
    const message = encodeURIComponent(`🎉 *Votre carte de visite ATOM est prête !*

🔗 Accédez à votre carte ici :
${publicUrl}

📱 *Pour ajouter un raccourci sur votre téléphone :*
• *iPhone* : Ouvrez le lien dans Safari → Appuyez sur "Partager" → "Sur l'écran d'accueil"
• *Android* : Ouvrez le lien dans Chrome → Menu (⋮) → "Ajouter à l'écran d'accueil"

En 1 tap, vous pourrez présenter votre carte à vos clients ! ✨`);
    
    // Open WhatsApp with pre-filled message
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const blob = await cardsService.downloadCardPdf(card.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `carte-${card.full_name.toLowerCase().replace(/\s+/g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Failed to download PDF:', error);
      alert('Erreur lors du téléchargement du PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <div className="w-16 h-16 border-4 border-[#2B4A6F] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Chargement...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!card) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-gray-400">Carte non trouvée</p>
        </div>
      </DashboardLayout>
    );
  }

  const publicUrl = `${window.location.origin}/card/${card.unique_id}`;

  return (
    <DashboardLayout>
      <div>
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center space-x-2 text-gray-400 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Retour</span>
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-white text-3xl font-bold mb-2">Détails de la carte</h1>
              <p className="text-gray-400">{card.full_name}</p>
            </div>
            <div className="flex items-center space-x-3">
              <Link
                to={`/cards/${cardId}/edit`}
                className="flex items-center space-x-2 bg-[#2B4A6F] hover:bg-[#355275] text-white px-4 py-2 rounded-lg transition-colors"
              >
                <Edit size={18} />
                <span>Modifier</span>
              </Link>
              <button
                onClick={handleDelete}
                className="flex items-center space-x-2 bg-red-500/10 hover:bg-red-500/20 text-red-500 px-4 py-2 rounded-lg transition-colors"
              >
                <Trash2 size={18} />
                <span>Supprimer</span>
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Card Preview */}
          <div className="lg:col-span-2 space-y-6">
            {/* Card Preview Image */}
            <div className="bg-[#0A0A0A] border border-gray-800 rounded-2xl p-6">
              <h2 className="text-white text-xl font-semibold mb-6">Aperçu de la carte</h2>
              <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
                {/* Photo */}
                <div className="flex-shrink-0">
                  {card.photo_base64 ? (
                    <img 
                      src={card.photo_base64.startsWith('data:') ? card.photo_base64 : `data:image/jpeg;base64,${card.photo_base64}`}
                      alt={card.full_name}
                      className="w-32 h-32 md:w-40 md:h-40 rounded-2xl object-cover shadow-lg border-2 border-gray-700"
                    />
                  ) : (
                    <div className="w-32 h-32 md:w-40 md:h-40 rounded-2xl bg-gradient-to-br from-[#2B4A6F] to-[#4A6B8F] flex items-center justify-center shadow-lg border-2 border-gray-700">
                      <span className="text-4xl md:text-5xl font-bold text-white">
                        {card.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Card Info Summary */}
                <div className="flex-1 text-center md:text-left">
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-1">{card.full_name}</h3>
                  <p className="text-[#7CB4E8] text-lg font-medium mb-1">{card.job_title}</p>
                  <p className="text-gray-400 mb-4">{card.company}</p>
                  
                  {/* Quick contact links */}
                  <div className="flex flex-wrap justify-center md:justify-start gap-2">
                    {card.email && (
                      <a 
                        href={`mailto:${card.email}`}
                        className="inline-flex items-center space-x-2 bg-[#2B4A6F]/20 hover:bg-[#2B4A6F]/30 text-[#7CB4E8] px-3 py-1.5 rounded-lg text-sm transition-colors"
                      >
                        <Mail size={14} />
                        <span>Email</span>
                      </a>
                    )}
                    {card.phone && (
                      <a 
                        href={`tel:${card.phone}`}
                        className="inline-flex items-center space-x-2 bg-green-500/20 hover:bg-green-500/30 text-green-400 px-3 py-1.5 rounded-lg text-sm transition-colors"
                      >
                        <Smartphone size={14} />
                        <span>Téléphone</span>
                      </a>
                    )}
                    {card.linkedin_url && (
                      <a 
                        href={card.linkedin_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center space-x-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 px-3 py-1.5 rounded-lg text-sm transition-colors"
                      >
                        <ExternalLink size={14} />
                        <span>LinkedIn</span>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Card Info */}
            <div className="bg-[#0A0A0A] border border-gray-800 rounded-2xl p-6">
              <h2 className="text-white text-xl font-semibold mb-6">Informations détaillées</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-400 text-sm mb-1">Nom complet</p>
                  <p className="text-white">{card.full_name}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Titre/Poste</p>
                  <p className="text-white">{card.job_title}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Entreprise</p>
                  <p className="text-white">{card.company}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm mb-1">Email</p>
                  <p className="text-white truncate">{card.email}</p>
                </div>
                {card.phone && (
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Téléphone</p>
                    <p className="text-white">{card.phone}</p>
                  </div>
                )}
                {card.mobile && (
                  <div>
                    <p className="text-gray-400 text-sm mb-1">Mobile</p>
                    <p className="text-white">{card.mobile}</p>
                  </div>
                )}
                {card.address && (
                  <div className="col-span-2">
                    <p className="text-gray-400 text-sm mb-1">Adresse</p>
                    <p className="text-white">{card.address}</p>
                  </div>
                )}
                {card.bio && (
                  <div className="col-span-2">
                    <p className="text-gray-400 text-sm mb-1">Bio</p>
                    <p className="text-white">{card.bio}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Analytics */}
            <div className="bg-[#0A0A0A] border border-gray-800 rounded-2xl p-6">
              <h2 className="text-white text-xl font-semibold mb-6 flex items-center space-x-2">
                <Eye size={24} />
                <span>Analytics</span>
              </h2>
              {analytics ? (
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-[#2B4A6F]/10 border border-[#2B4A6F]/20 rounded-xl p-4">
                    <Eye size={24} className="text-[#7CB4E8] mb-2" />
                    <p className="text-3xl font-bold text-white mb-1">{analytics.views_count}</p>
                    <p className="text-gray-400 text-sm">Vues totales</p>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4">
                    <MousePointerClick size={24} className="text-green-500 mb-2" />
                    <p className="text-3xl font-bold text-white mb-1">{analytics.clicks_count}</p>
                    <p className="text-gray-400 text-sm">Clics totaux</p>
                  </div>
                  <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-4">
                    <Share2 size={24} className="text-purple-500 mb-2" />
                    <p className="text-3xl font-bold text-white mb-1">
                      {analytics.clicks_count > 0 ? Math.round((analytics.clicks_count / analytics.views_count) * 100) : 0}%
                    </p>
                    <p className="text-gray-400 text-sm">Taux d'engagement</p>
                  </div>
                </div>
              ) : (
                <p className="text-gray-400 text-center py-8">Aucune donnée analytique pour le moment</p>
              )}
            </div>
          </div>

          {/* Right Column - QR Code & Sharing */}
          <div className="space-y-6">
            {/* QR Code */}
            <div className="bg-[#0A0A0A] border border-gray-800 rounded-2xl p-6">
              <h2 className="text-white text-xl font-semibold mb-6">QR Code</h2>
              {card.qr_code_base64 && (
                <div className="flex flex-col items-center">
                  <div className="bg-white p-4 rounded-2xl mb-4">
                    <img src={card.qr_code_base64} alt="QR Code" className="w-48 h-48" />
                  </div>
                  <p className="text-gray-400 text-sm text-center">Scannez ce code pour accéder à la carte</p>
                </div>
              )}
            </div>

            {/* Share */}
            <div className="bg-[#0A0A0A] border border-gray-800 rounded-2xl p-6">
              <h2 className="text-white text-xl font-semibold mb-6">Partager</h2>
              <div className="space-y-3">
                <div className="bg-black/50 border border-gray-700 rounded-lg p-3">
                  <p className="text-gray-400 text-xs mb-2">Lien public</p>
                  <p className="text-white text-sm break-all">{publicUrl}</p>
                </div>
                <button
                  onClick={copyPublicUrl}
                  className="w-full flex items-center justify-center space-x-2 bg-[#2B4A6F] hover:bg-[#355275] text-white py-3 rounded-lg transition-colors"
                >
                  {copied ? (
                    <>
                      <Check size={20} />
                      <span>Copié !</span>
                    </>
                  ) : (
                    <>
                      <Copy size={20} />
                      <span>Copier le lien</span>
                    </>
                  )}
                </button>
                <button
                  onClick={openPublicCard}
                  className="w-full flex items-center justify-center space-x-2 bg-gray-800 hover:bg-gray-700 text-white py-3 rounded-lg transition-colors"
                >
                  <ExternalLink size={20} />
                  <span>Ouvrir la carte publique</span>
                </button>
                <button
                  onClick={handleDownloadPdf}
                  disabled={downloadingPdf}
                  className="w-full flex items-center justify-center space-x-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 border border-purple-500/30 py-3 rounded-lg transition-colors disabled:opacity-50"
                >
                  <FileText size={20} />
                  <span>{downloadingPdf ? 'Génération...' : 'Télécharger PDF'}</span>
                </button>
              </div>
            </div>

            {/* Send to Advisor */}
            <div className="bg-gradient-to-br from-[#3B9EFF]/10 to-cyan-500/5 border border-[#3B9EFF]/30 rounded-2xl p-6">
              <h2 className="text-white text-xl font-semibold mb-2 flex items-center space-x-2">
                <Send size={20} className="text-[#3B9EFF]" />
                <span>Envoyer au conseiller</span>
              </h2>
              <p className="text-gray-400 text-sm mb-4">
                Envoyez le lien de la carte au conseiller pour qu'il puisse l'utiliser sur le terrain.
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => setShowSendModal(true)}
                  className="w-full flex items-center justify-center space-x-2 bg-[#3B9EFF] hover:bg-[#2B8EEF] text-white py-3 rounded-lg transition-colors font-medium"
                >
                  <Mail size={20} />
                  <span>Envoyer par email</span>
                </button>
                <button
                  onClick={handleSendByWhatsApp}
                  className="w-full flex items-center justify-center space-x-2 bg-[#25D366] hover:bg-[#20BD5A] text-white py-3 rounded-lg transition-colors font-medium"
                >
                  <MessageCircle size={20} />
                  <span>Envoyer par WhatsApp</span>
                </button>
              </div>
            </div>

            {/* NFC Tag Section */}
            <div className="bg-gradient-to-br from-orange-500/10 to-amber-500/5 border border-orange-500/30 rounded-2xl p-6">
              <h2 className="text-white text-xl font-semibold mb-2 flex items-center space-x-2">
                <Wifi size={20} className="text-orange-500" />
                <span>Tag NFC</span>
              </h2>
              <p className="text-gray-400 text-sm mb-4">
                Programmez un tag NFC pour partager votre carte d'un simple tap !
              </p>
              <div className="space-y-3">
                <button
                  onClick={() => setShowNfcModal(true)}
                  className="w-full flex items-center justify-center space-x-2 bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-lg transition-colors font-medium"
                >
                  <Smartphone size={20} />
                  <span>Configurer un tag NFC</span>
                </button>
              </div>
            </div>

            {/* Status */}
            <div className="bg-[#0A0A0A] border border-gray-800 rounded-2xl p-6">
              <h2 className="text-white text-xl font-semibold mb-4">Statut</h2>
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${card.is_public ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <span className="text-gray-300">
                  {card.is_public ? 'Carte publique' : 'Carte privée'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Send Email Modal */}
      {showSendModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#111] border border-gray-800 rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-white text-xl font-semibold mb-2 flex items-center space-x-2">
              <Mail size={24} className="text-[#3B9EFF]" />
              <span>Envoyer au conseiller</span>
            </h3>
            <p className="text-gray-400 text-sm mb-6">
              Envoyez le lien de la carte avec les instructions pour ajouter le raccourci sur téléphone.
            </p>
            
            {sendSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check size={32} className="text-green-500" />
                </div>
                <p className="text-white font-medium">Email envoyé avec succès !</p>
                <p className="text-gray-400 text-sm">Le conseiller recevra le lien sous peu.</p>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <label className="block text-gray-300 text-sm font-medium mb-2">
                    Email du conseiller
                  </label>
                  <input
                    type="email"
                    value={sendEmail}
                    onChange={(e) => setSendEmail(e.target.value)}
                    className="w-full bg-black/50 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#3B9EFF] focus:ring-1 focus:ring-[#3B9EFF]/50 transition-all"
                    placeholder={card.email}
                  />
                  <p className="text-gray-500 text-xs mt-2">
                    💡 Un email avec le lien et les instructions sera généré.
                  </p>
                </div>
                
                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowSendModal(false)}
                    className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleSendToAdvisor}
                    disabled={sending}
                    className="flex-1 py-3 bg-[#3B9EFF] hover:bg-[#2B8EEF] text-white rounded-xl transition-colors font-medium flex items-center justify-center space-x-2"
                  >
                    <Send size={18} />
                    <span>{sending ? 'Envoi...' : 'Envoyer'}</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* NFC Modal */}
      {showNfcModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-[#111] border border-gray-800 rounded-2xl p-6 max-w-lg w-full my-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white text-xl font-semibold flex items-center space-x-2">
                <Wifi size={24} className="text-orange-500" />
                <span>Configurer un Tag NFC</span>
              </h3>
              <button
                onClick={() => setShowNfcModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            <p className="text-gray-400 text-sm mb-6">
              Un tag NFC permet à vos clients de récupérer vos coordonnées d'un simple tap avec leur smartphone !
            </p>

            {/* Link to program */}
            <div className="bg-black/50 border border-gray-700 rounded-xl p-4 mb-6">
              <p className="text-gray-400 text-xs mb-2 uppercase tracking-wider">Lien à programmer sur le tag</p>
              <div className="flex items-center space-x-2">
                <code className="flex-1 text-orange-400 text-sm break-all bg-black/30 px-3 py-2 rounded-lg">
                  {publicUrl}
                </code>
                <button
                  onClick={copyNfcUrl}
                  className={`p-2 rounded-lg transition-colors ${nfcCopied ? 'bg-green-500/20 text-green-500' : 'bg-orange-500/20 text-orange-500 hover:bg-orange-500/30'}`}
                >
                  {nfcCopied ? <Check size={20} /> : <Copy size={20} />}
                </button>
              </div>
              {nfcCopied && (
                <p className="text-green-500 text-xs mt-2">✓ Lien copié !</p>
              )}
            </div>

            {/* Instructions */}
            <div className="space-y-4 mb-6">
              <h4 className="text-white font-semibold">Comment programmer le tag ?</h4>
              
              {/* iPhone */}
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center">
                    <span className="text-lg">🍎</span>
                  </div>
                  <span className="text-white font-medium">iPhone (iOS 13+)</span>
                </div>
                <ol className="text-gray-400 text-sm space-y-2 list-decimal list-inside">
                  <li>Téléchargez l'app <span className="text-orange-400 font-medium">NFC Tools</span> sur l'App Store</li>
                  <li>Ouvrez l'app et appuyez sur <span className="text-white">Écrire</span></li>
                  <li>Sélectionnez <span className="text-white">Ajouter un enregistrement</span> → <span className="text-white">URL</span></li>
                  <li>Collez le lien ci-dessus</li>
                  <li>Approchez le tag NFC de votre iPhone (en haut)</li>
                  <li>Appuyez sur <span className="text-white">Écrire</span></li>
                </ol>
              </div>

              {/* Android */}
              <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center">
                    <span className="text-lg">🤖</span>
                  </div>
                  <span className="text-white font-medium">Android</span>
                </div>
                <ol className="text-gray-400 text-sm space-y-2 list-decimal list-inside">
                  <li>Téléchargez l'app <span className="text-orange-400 font-medium">NFC Tools</span> sur le Play Store</li>
                  <li>Ouvrez l'app et appuyez sur <span className="text-white">Écrire</span></li>
                  <li>Appuyez sur <span className="text-white">Ajouter un enregistrement</span> → <span className="text-white">URL</span></li>
                  <li>Collez le lien ci-dessus</li>
                  <li>Approchez le tag NFC de votre téléphone</li>
                  <li>Le tag sera programmé automatiquement</li>
                </ol>
              </div>
            </div>

            {/* Buy NFC Tags */}
            <div className="bg-gradient-to-r from-orange-500/10 to-amber-500/10 border border-orange-500/30 rounded-xl p-4 mb-6">
              <h4 className="text-white font-medium mb-2 flex items-center space-x-2">
                <ShoppingCart size={18} className="text-orange-500" />
                <span>Où acheter des tags NFC ?</span>
              </h4>
              <p className="text-gray-400 text-sm mb-3">
                Tags NFC NTAG215 recommandés (compatibles tous smartphones) :
              </p>
              <div className="flex flex-wrap gap-2">
                <a
                  href="https://www.amazon.fr/s?k=nfc+tag+ntag215"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-1 bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 px-3 py-1.5 rounded-lg text-sm transition-colors"
                >
                  <span>Amazon</span>
                  <ExternalLink size={14} />
                </a>
                <a
                  href="https://www.digitec.ch/fr/search?q=nfc+tag"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-1 bg-orange-500/20 hover:bg-orange-500/30 text-orange-400 px-3 py-1.5 rounded-lg text-sm transition-colors"
                >
                  <span>Digitec</span>
                  <ExternalLink size={14} />
                </a>
              </div>
              <p className="text-gray-500 text-xs mt-3">
                💡 Astuce : Collez le tag au dos de votre téléphone, sur votre badge ou votre porte-cartes !
              </p>
            </div>

            <button
              onClick={() => setShowNfcModal(false)}
              className="w-full py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-xl transition-colors font-medium"
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default CardDetail;
